import { useEffect, useRef, useState } from 'react';
import styles from './RateCard.module.scss';
import {
	Breadcrumbs,
	Button,
	ErrorFallback,
	FuelLevySettingsPanel,
	Grid,
	InfoChip,
	Loader,
	PageTitle,
	Select,
	Sidebar,
	Spacer,
	StickyFooter,
	StickyHeader,
	Text,
	toast,
	Dialog,
	H1,
	H5,
} from '@components';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useFeatureFlag, useHasRole, useImpersonation, useIsInRoute, useWithImpersonation } from '@hooks';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import { RateCardHeader } from './Header';
import { ConfigureRates } from './ConfigureRates';
import {
	BUTTON_SIZE,
	BUTTON_TYPES,
	CHIP_TYPE,
	RATE_CARD_ROLES,
	RateCardHistory,
	SelectOptions,
	SettingType,
	TEXT_WEIGHT,
	TLinkedRateCard
} from '@types';
import {
	useGetLane,
	useGetRateCardGlobalSettings,
	useGetRateCardSettings,
	useGetShipmentTags,
	useSaveLane,
	useUpdateLane,
	usePostPreviewCustomerLaneWithMargin,
	useCreateLinkedLane,
	useUpdateLinkedLane,
	useGetLinkedLaneConfiguration,
	useDeleteRateCard,
	useApproveRateCards,
} from '@api';
import {
	ActiveCarrier,
	ActiveShipper,
	RateCard as RateCardState,
	RateCardFormErrors,
	RateCardGlobalSettings,
	RateCardSettings as RateCardSettingsState,
	LinkedRateCard,
	XCustomerID,
	RateCardFamily,
	LinkedRateCardPayloadSelector,
	GlobalRateCardId
} from '@state';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
	buildLaneAndValidate,
	convertErrorObject,
	DATE_FORMATS,
	formatDate,
	formatIsoDateTimeToSydneyTime,
	getLaneFuelLevyId,
	getSelectedFuelLevy,
	parseAndFormatDate,
} from '@utils';
import { TLaneSchema } from '@utils/buildLaneSchema';
import { ZodFormattedError } from 'zod';
import { useConvertQueryStringToLane } from '@hooks/useConvertQueryStringToLane';
import ShipperRateCardHistory from '@pages/Shipper/RateCards/History';
import { format } from 'date-fns';
import { Logout, VisibilityOutlined } from '@mui/icons-material';
import { RateCardStepper } from '../RC_LinkedRateCard/RateCardStepper';
import { LinkedRateCardSummary } from '../RC_LinkedRateCard';

type TRateCardProps = {
	settingType: SettingType;
	readonly?: boolean;
	history?: RateCardHistory;
	version?: string|null;
	backToLiveVersion?: () => void;
	isLinkedRateCard?: boolean;
}

export const RateCard = (Props: TRateCardProps) => {
	const {
		settingType,
		readonly = false,
		history,
		version = null,
		backToLiveVersion = () => {},
		isLinkedRateCard = false
	} = Props;

	const rateCardId = useRecoilValue(GlobalRateCardId);
	const useFamily = rateCardId !== 'default';

	const [rateCard, setRateCard] = useRecoilState(
		useFamily ? RateCardFamily(rateCardId) : RateCardState
	);
	// Direct setter for shipper rate card (always targets 'shipper' instance)
	const setShipperRateCard = useSetRecoilState(RateCardFamily('shipper'));

	const isLinked = isLinkedRateCard || rateCard?.lane_link?.external_id;

	const containerRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();
	const params = useParams();
	const [searchParams] = useSearchParams();
	const location = useLocation();

	const isStep2 = useIsInRoute('step2');
	const isStep3 = useIsInRoute('step3');
	const isLinkedCarrierRateCardRoute = !!isLinked && isStep2;
	const isLinkedShipperRateCardRoute = !!isLinked && isStep3;

	const carrierInRoute = useIsInRoute('carrier') || isLinkedCarrierRateCardRoute
	const shipperInRoute = useIsInRoute('shipper') || isLinkedShipperRateCardRoute
	const withImpersonation = useWithImpersonation(shipperInRoute ? 'shipper' : 'carrier');
	const adminRoute = !carrierInRoute && !shipperInRoute;
	const isCreateRateCard = useIsInRoute('create');
	const isUpdateRateCard = useIsInRoute('update');
	const activeShipper = useRecoilValue(ActiveShipper);
	const activeCarrier = useRecoilValue(ActiveCarrier);
	const linkedRateCardData = useRecoilValue(LinkedRateCard);
	const [isAlertInfoOpen, setIsAlertInfoOpen] = useState(false);
	const isAdminRoute = !carrierInRoute && !shipperInRoute;
	const customerFuelLevyFeatureFlag = useFeatureFlag('customer_fuel_levy');
	const laneData = params.id ?? null;

	// use the correct external_id as per the route when rendering the page
	const linkedRateCardExternalId = isLinkedCarrierRateCardRoute
		? linkedRateCardData?.carrier?.external_id
		: isLinkedShipperRateCardRoute
			? linkedRateCardData?.shipper?.external_id
			: undefined;

	const setRcSettings = useSetRecoilState(RateCardSettingsState);
	const setRcGlobalSettings = useSetRecoilState(RateCardGlobalSettings);
	const queryToLane = useConvertQueryStringToLane(laneData ?? null, isLinkedRateCard);
	const laneDataObject = isCreateRateCard && laneData ? queryToLane : null;
	const [shipperTag, setShipperTag] = useState<string | null>(null);
	const [initialLoad, setInitialLoad] = useState(false);
	const [viewHistory, setViewHistory] = useState(false);
	const setRcFormErrors = useSetRecoilState(RateCardFormErrors);

	const [versionOptions, setVersionOptions] = useState<SelectOptions[]>();
	const [selectedVersion, setSelectedVersion] = useState<string | null>(version);
	const [showRedirectMessage, setShowRedirectMessage] = useState(false);
	const [previousUpdatedAt, setPreviousUpdatedAt] = useState<string | null>(null);

	const { loadingApproveRateCards, callApproveRateCards } = useApproveRateCards();
	const { loadingDeleteRC, callDeleteRateCard } = useDeleteRateCard();
	const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

	const linkedRateCardPayload = useRecoilValue(LinkedRateCardPayloadSelector);
	const setLinkedRateCardData = useSetRecoilState(LinkedRateCard);
	const { impersonate } = useImpersonation();

	useEffect(() => {
		if (version !== selectedVersion) {
			setSelectedVersion(version);
		}
	}, [version]);

	useEffect(() => {
		if (showRedirectMessage && rateCard && !location.search.includes('version') && rateCard?.updated_at !== previousUpdatedAt) {
			const date = formatDate(new Date(rateCard.updated_at ?? ''), DATE_FORMATS.DATE_WITH_SHORT_MONTH_NAME)
			const time = rateCard?.updated_at ? formatIsoDateTimeToSydneyTime(rateCard.updated_at) : '-'
			toast.success(`You are now viewing the current version. Last updated on ${date} at ${time}`);
			setShowRedirectMessage(false);
		}
	}, [showRedirectMessage, rateCard]);

	useEffect(() => {
		if (location.search.includes('live') && rateCard?.updated_at) {
			setPreviousUpdatedAt(rateCard.updated_at);
			setShowRedirectMessage(true);
		}
	}, [searchParams]);

	const canEditRateCard = useHasRole(
		adminRoute ? RATE_CARD_ROLES.ADMIN_RATECARD_MANAGER :
			shipperInRoute ? RATE_CARD_ROLES.SHIPPER_RATE_CARD_MANAGER :
				RATE_CARD_ROLES.CARRIER_RATE_CARD_MANAGER
	);
	const {
		isLoadingRateCardSettings,
		getErrorRateCardSettings,
		callGetRateCardSettings,
	} = useGetRateCardSettings();
	const {
		isLoadingGetLane,
		getGetLaneError,
		callGetLane
	} = useGetLane();
	const {
		isLoadingSaveLane,
		callSaveLane,
	} = useSaveLane();
	const {
		loadingPreviewCustomerLaneWithMargin,
		callPostPreviewCustomerLaneWithMargin,
	} = usePostPreviewCustomerLaneWithMargin();
	const { loadingCreateLinkedLane, callCreateLinkedLane } = useCreateLinkedLane();
	const { loadingUpdateLinkedLane, callUpdateLinkedLane } = useUpdateLinkedLane();
	const {
		isLoadingUpdateLane,
		callUpdateLane,
	} = useUpdateLane();
	const {
		isLoadingRateCardGlobalSettings,
		getErrorRateCardGlobalSettings,
		callGetRateCardGlobalSettings,
	} = useGetRateCardGlobalSettings();
	const { callGetLinkedLaneConfiguration } = useGetLinkedLaneConfiguration();
	const CustomerId = useRecoilValue(XCustomerID) ?? '';
	const callGetRCSettings =() => {
		callGetRateCardSettings({
			headers: linkedRateCardExternalId ? { 'X-Customer-Id': linkedRateCardExternalId } : { 'X-Customer-Id': CustomerId },
			routes: [isAdminRoute ? 'admin' : 'customer']
		})
			.then((response) => {
				if (!response?.data) return
				setRcSettings(response.data);

			}).catch((err) => {
				toast.error(err.cause.response.data?.message ?? 'An error occurred fetching Rate Cards');
			}).finally(() => {
				if (!initialLoad) {
					setInitialLoad(true);
				}
			});
	}
	const callGetGlobalSettingsAPI = () => {
		callGetRateCardGlobalSettings({ routes: [isAdminRoute ? 'admin' : shipperInRoute ? 'shipper' : 'carrier'] })
			.then((response) => {
				if (!response?.data) return;
				setRcGlobalSettings(response.data);
			}).catch((err) => {
				toast.error(err.cause.response.data?.message ?? 'An error occurred fetching rate card global settings');
			}).finally(() => {
				if (!initialLoad) {
					setInitialLoad(true);
				}
			});
	}
	const {
		tagsList,
		callGetShipmentTags,
	} = useGetShipmentTags();

	const generateNonAdminRoute = () => {
		const customerType = shipperInRoute ? 'shipper' : 'carrier';
		const queryParam = customerType === 'shipper' ? `?sid=${activeShipper?.id}` : `?cid=${activeCarrier?.id}`;
		return `/${customerType}/ratecards${queryParam}`;
	}

	const cleanLinkedLanePayload = (payload: TLaneSchema, externalId?: string) => {
		const {
			freight_mode,
			origin_location,
			destination_location,
			...rest
		} = payload;

		return {
			...rest,
			customer_external_id: externalId
		};
	};

	// onSaveSuccess: if provided, replaces the default toast+navigate on success (used by approve flow)
	const handleSaveClick = (onSaveSuccess?: () => void) => {
		if (!rateCard) return;
		if (!rateCard.rate_cards.length) {
			toast.error('Please add at least one rate card type');
			return;
		}
		if (customerFuelLevyFeatureFlag && rateCard.fuel_levy_type === 'customer' && !getLaneFuelLevyId(rateCard)) {
			toast.error('Please select a fuel levy.');
			containerRef?.current?.parentElement?.scrollIntoView({ behavior: 'smooth' });
			return;
		}
		if ((
			rateCard.fuel_levy_type === 'customer' &&
			!getSelectedFuelLevy(rateCard?.fuel_levies, 'customer', getLaneFuelLevyId(rateCard))?.current
		)
		|| (
			rateCard.fuel_levy_type === 'rate_card' && !rateCard?.fuel_levies?.rate_card?.current
		)) {
			toast.error('Please add fuel levy value.');
			containerRef?.current?.parentElement?.scrollIntoView({ behavior: 'smooth' });
			return;
		}
		let shipperTagObject = null;
		if (shipperTag && tagsList.find((tag) => tag.external_id === shipperTag)) {
			shipperTagObject = tagsList.find((tag) => tag.external_id === shipperTag) ?? null;
		}
		const linkedLaneExternalId = isLinked && linkedRateCardData?.linked_lane_external_id ? linkedRateCardData?.linked_lane_external_id : '';
		const { success, errors, payload } = buildLaneAndValidate(
			{
				...rateCard,
				shipper_tag: shipperTagObject
			},
			isAdminRoute ? 'admin' : shipperInRoute ? 'shipper' : 'carrier',
			'lane',
			!!isLinked,
			linkedLaneExternalId,
		);

		if (success) {
			if (rateCard?.external_id) {
				if (isLinkedCarrierRateCardRoute) {
					/* if it's step2, since the state will update to carrier ratecard
					   only call preview shipper ratecard data and then redirect to step3 */
					const extendedPayload = { ...payload, margin_percentage: linkedRateCardData?.margin_percentage ?? 0 }
					if (linkedRateCardData?.carrier.external_id && linkedRateCardData?.carrier.external_id && linkedRateCardData?.shipper?.external_id) {
						callPostPreviewCustomerLaneWithMargin({
							headers: { 'X-Customer-Id': linkedRateCardData?.carrier.external_id },
							payload: extendedPayload,
							routes: [linkedRateCardData?.shipper?.external_id],
						}).then(({ data }) => {
							if (data) {
								setShipperRateCard(data.data)

								if (linkedRateCardData?.shipper_lane_id) {
									setTimeout(() => {
										navigate(`/configuration/ratecards/linked/update/step3/${linkedRateCardData?.shipper_lane_id}`);
									}, 500);
								} else {
									setTimeout(() => {
										navigate(`/configuration/ratecards/linked/create/step3`);
									}, 500);
								}
							} else {
								console.error('Error when apply margin on rate card data')
							}
						})

						return;
					} else {
						console.error('Missing linkedRateCardData data in state', linkedRateCardData);
					}

				} else if (isLinkedShipperRateCardRoute
					&& linkedRateCardPayload?.carrier?.external_id
					&& linkedRateCardPayload?.shipper?.external_id
					&& linkedRateCardData?.carrier?.external_id
					&& linkedRateCardData?.shipper?.external_id
					&& linkedRateCardData?.linked_lane_external_id
				) {
					/* if it's step3
					   send api request and update both shipper/carrier ratecard simultaneously */
					const carrierLane = buildLaneAndValidate(
						linkedRateCardPayload?.carrier,
						'carrier',
						'lane',
					);
					const shipperLane = buildLaneAndValidate(
						linkedRateCardPayload?.shipper,
						'shipper',
						'lane',
					)
					if (!carrierLane.success || !shipperLane.success) {
						toast.error('Invalid payload for updating linked lane');
						console.error(carrierLane.errors)
						console.error(shipperLane.errors)
					} else {
						const updatePayload = {
							carrier: cleanLinkedLanePayload(carrierLane.payload, linkedRateCardData?.carrier.external_id),
							shipper: cleanLinkedLanePayload(shipperLane.payload, linkedRateCardData?.shipper.external_id),
							margin_percentage: linkedRateCardData.margin_percentage,
						}
						callUpdateLinkedLane({
							payload: updatePayload,
							routes: [linkedRateCardData?.linked_lane_external_id],
						}).then(() => {
							const message = `Updated ${linkedRateCardData.shipper.name}/${linkedRateCardData.carrier.name} rate cards for this configuration`;
							toast.success(message, {
								title: 'Successfully updated linked Rate Cards',
								keepToastWhenLocationChange: true,
							});
							setTimeout(() => {
								navigate('/configuration/ratecards?type=linked');
							}, 500);
						})
					}
				} else {
					callUpdateLane({
						payload: { ...payload, expected_updated_at: rateCard.updated_at },
						routes: [rateCard?.external_id],
						returnFullError: true
					})
						.then(() => {
							if (onSaveSuccess) {
								onSaveSuccess();
							} else {
								toast.success('Rate cards updated successfully');
								setTimeout(() => {
									navigate(isAdminRoute ? '/configuration/ratecards' : generateNonAdminRoute());
								}, 500);
							}
						})
						.catch((err) => {
							const errorData = err.cause?.response?.data;
							if (err.cause?.response?.status === 409) {
								toast.error(
									errorData?.message ?? 'This rate card was changed by someone else while you were editing. Reload the page to get the latest rates, then reapply your changes.',
									{ title: 'Rate card out of date' }
								);
								return;
							}
							toast.error('There has been an error trying to save the rate card');
							if (errorData?.errors) {
								const nestedErrors = convertErrorObject(errorData.errors);
								setRcFormErrors(nestedErrors)
							}
						})
				}
			} else {
				// check if linked rate card
				if (isLinked) {
					if (isLinkedCarrierRateCardRoute) {
						/* if it's step2
						   trigger callPostPreviewCustomerLane, and setShipperRateCard
						   redirect to step3 */
						const extendedPayload = { ...payload, margin_percentage: linkedRateCardData?.margin_percentage ?? 0 }
						if (linkedRateCardData?.carrier.external_id && linkedRateCardData?.shipper?.external_id) {
							callPostPreviewCustomerLaneWithMargin({
								headers: { 'X-Customer-Id': linkedRateCardData?.carrier.external_id },
								payload: extendedPayload,
								routes: [linkedRateCardData?.shipper?.external_id],
							}).then(({ data }) => {

								if (data) {
									setShipperRateCard(data.data)
								}
							})

							setTimeout(() => {
								navigate(`/configuration/ratecards/linked/create/step3`);
							}, 500);
						} else {
							console.error('Missing linkedRateCardData data in state', linkedRateCardData);
						}
					} else if (isLinkedShipperRateCardRoute && linkedRateCardPayload?.carrier && linkedRateCardPayload?.shipper) {
						/* if it's step3
						   trigger api call and save both carrier and shipper ratecards at the same time
						   redirect to linked ratecard list page */
						const carrierLane = buildLaneAndValidate(
							linkedRateCardPayload?.carrier,
							'carrier',
							'lane',
							true, // always include all the create fields
						);
						const shipperLane = buildLaneAndValidate(
							linkedRateCardPayload?.shipper,
							'shipper',
							'lane',
							true, // always include all the create fields
						)

						if (!carrierLane.success || !shipperLane.success) {
							toast.error('Invalid payload for creating linked lane');
							console.error(carrierLane.errors)
							console.error(shipperLane.errors)
						} else {
							callCreateLinkedLane({
								payload: {
									carrier: { ...carrierLane.payload, customer_external_id: linkedRateCardData?.carrier.external_id },
									shipper: { ...shipperLane.payload, customer_external_id: linkedRateCardData?.shipper.external_id },
									margin_percentage: linkedRateCardData?.margin_percentage ?? 0,
									is_attach: linkedRateCardData?.carrier_lane_id !== null,
								}
							}).then(() => {
								if (linkedRateCardData) {
									const message = `Generated ${linkedRateCardData.shipper.name}/${linkedRateCardData.carrier.name} rate cards for this configuration`;
									toast.success(message, {
										title: 'Successfully created linked Rate Cards',
										keepToastWhenLocationChange: true,
									});
								}

								setTimeout(() => {
									navigate('/configuration/ratecards?type=linked');
								}, 500);
							})
						}
					}
				} else {
					// Existing save lane logic, nothing change
					callSaveLane({
						headers: linkedRateCardExternalId ? { 'X-Customer-Id': linkedRateCardExternalId } : { 'X-Customer-Id': CustomerId },
						payload,
						routes: [isAdminRoute ? 'admin' : 'customer'],
						returnFullError: true
					})
						.then(() => {
							if (onSaveSuccess) {
								onSaveSuccess();
							} else {
								toast.success('Rate cards saved successfully');
								setTimeout(() => {
									navigate(isAdminRoute ? '/configuration/ratecards' : generateNonAdminRoute());
								}, 500);
							}
						})
						.catch((err) => {
							toast.error('There has been an error trying to save the rate card');
							const errorData = err.cause?.response?.data;
							if (errorData?.errors) {
								const nestedErrors = convertErrorObject(errorData.errors);
								setRcFormErrors(nestedErrors)
							}
						})
				}
			}
		} else {
			containerRef?.current?.parentElement?.scrollIntoView({ behavior: 'smooth' });
			console.error(errors)
			setRcFormErrors(errors as ZodFormattedError<TLaneSchema>);
			toast.error('Please review the errors and try again.')
		}
	}

	const breadCrumbs = isLinked ? [
		{
			text: 'Configuration',
			url: '/configuration'
		},
		{
			text: 'Rate Cards',
			url: '/configuration/ratecards'
		},
		{
			text: 'Create Linked Rate Card',
			url: '/configuration/ratecards/linked/create/step1',
			disabled: isUpdateRateCard === true,
		},
		{
			text: 'Step 1 | Rate Card Details',
			url: '/configuration/ratecards/linked/create/step1',
			disabled: isUpdateRateCard === true,
		},
		{
			text: 'Step 2 | Carrier ',
			url: isUpdateRateCard ? `/configuration/ratecards/linked/update/step2/${linkedRateCardData?.carrier_lane_id}` : '/configuration/ratecards/linked/create/step2'
		},
		// Conditionally inject the step 3 in breadcrumb
		...(isLinkedShipperRateCardRoute ? [
			{
				text: 'Step 3 | Shipper Rule',
				url: ''
			},
		]: [])
	] : isAdminRoute ? [
		{
			text: 'Ratecards',
			url: `/configuration/ratecards`,
		},
		{
			text: 'Configure',
			url: ''
		}
	] : [
		{
			text: shipperInRoute ? `${activeShipper?.name ?? 'Shipper'}` : `${activeCarrier?.name ?? 'Carrier'}`,
			url: withImpersonation(shipperInRoute ? '/shipper/dashboard' : '/carrier/overview'),
		},
		{
			text: 'Ratecards',
			url: `/${shipperInRoute ? 'shipper' : 'carrier'}/ratecards?${shipperInRoute ? (`sid=${activeShipper?.id}`) : `cid=${activeCarrier?.id}`}`,
		},
		{
			text: 'Configure',
			url: ''
		}
	];

	const populateVersionDropdown = () => {
		const versions = history?.data?.map((version) => {
			return {
				label: parseAndFormatDate(version.actioned_at, 'DATE_WITH_SHORT_MONTH_TIME', 'ISO'),
				value: version.external_id,
				selected: version.external_id === selectedVersion,
			} as SelectOptions
		});


		// If they land on the page with a current rate card or version not in the history - redirect
		if (version && versions && !versions.find((version) => version.selected)) {
			backToLiveVersion()
		}

		setVersionOptions(versions)
	}

	useEffect(() => {
		if (history) {
			populateVersionDropdown();
		}
	}, [history])

	const updateDisplayedVersion = () => {
		if (laneData && selectedVersion) {
			const newSearchParams = new URLSearchParams(location.search);
			newSearchParams.set('version', selectedVersion);
			callGetLane({ routes: [laneData], params: newSearchParams } ).then((response) => {
				if (response?.data) {
					setRateCard({
						...response?.data?.data,
						end_date: (response.data.data.external_id && response.data.data.end_date) ? format(new Date(response.data.data.end_date), DATE_FORMATS.SHORT_DATE) : '',
					});
				}
			}).catch(() => {
				toast.error('There was an error trying to update version list');
			})
		}
	}

	const renderFooterButtons = () => {
		if (rateCard?.is_active === false) {
			return (
				<>
					<Button
						text={'Delete'}
						onClick={() => setIsDeleteDialogOpen(true)}
						type={BUTTON_TYPES.SECONDARY}
						loading={loadingDeleteRC}
						disabled={!canEditRateCard}
					/>
					<Button
						text={'Approve & Save'}
						onClick={() => setIsApproveDialogOpen(true)}
						loading={loadingApproveRateCards}
						disabled={!canEditRateCard}
					/>
				</>
			);
		}

		return (
			<>
				{!(isLinked && isUpdateRateCard && isLinkedCarrierRateCardRoute) && (
					<Button
						text={'Back'}
						onClick={() => navigate(-1)}
						type={BUTTON_TYPES.SECONDARY}
					/>
				)}
				<Button
					text={!!isLinked && isLinkedCarrierRateCardRoute ? 'Save & Continue' : 'Save Lane'}
					onClick={() => {
						!!isLinked && isLinkedShipperRateCardRoute ? setIsAlertInfoOpen(true) : handleSaveClick()
					}}
					loading={isLoadingGetLane || isLoadingSaveLane || isLoadingUpdateLane || loadingPreviewCustomerLaneWithMargin || loadingCreateLinkedLane || loadingUpdateLinkedLane}
					disabled={!canEditRateCard || readonly}
				/>
			</>
		);
	};

	const handleApprove = () => {
		if (!rateCard?.external_id) return;
		const externalId = rateCard.external_id;
		handleSaveClick(() => {
			callApproveRateCards({ payload: { lane_external_ids: [externalId] } })
				.then(() => {
					toast.success('Rate card approved successfully');
					setIsApproveDialogOpen(false);
					setTimeout(() => {
						navigate(isAdminRoute ? '/configuration/ratecards' : generateNonAdminRoute());
					}, 500);
				})
				.catch((err) => {
					toast.error(err.cause?.response?.data?.message || 'An error occurred trying to approve this rate card');
				});
		});
	};

	const handleDelete = () => {
		if (!rateCard?.external_id) return;
		callDeleteRateCard({ routes: [rateCard.external_id] })
			.then(() => {
				toast.success('Rate card deleted successfully');
				setIsDeleteDialogOpen(false);
				setTimeout(() => {
					navigate(isAdminRoute ? '/configuration/ratecards' : generateNonAdminRoute());
				}, 500);
			})
			.catch((err) => {
				toast.error(err.cause?.response?.data?.message ?? 'An error occurred trying to delete this rate card');
			});
	};

	useEffect(() => {
		if (selectedVersion) {
			updateDisplayedVersion();
		}
	}, [selectedVersion])

	/**
	 * If redirect from transport section and it contains "laneLinkExternalId"
	 * We need to reset the activeCarrier, activeShipper, and also update the LinkedRateCardData at recoil state
	 * @param externalId
	 */
	const getLinkedLaneConfigurationAndImpersonate = async (externalId: string) => {
		try {
			const response = await callGetLinkedLaneConfiguration({ routes: [externalId] })
			const linkedLane: TLinkedRateCard | undefined = response?.data?.data;

			if (linkedLane) {
				if (linkedLane.carrier?.external_id) {
					await impersonate('carrier', linkedLane.carrier?.external_id)
				}
				if (linkedLane.shipper?.external_id) {
					await impersonate('shipper', linkedLane.shipper?.external_id)
				}
				setLinkedRateCardData({
					linked_lane_external_id: linkedLane.linked_lane_external_id ?? '',
					carrier: {
						external_id: linkedLane.carrier?.external_id ?? '',
						name: linkedLane.carrier?.name ?? '',
					},
					shipper: {
						external_id: linkedLane.shipper?.external_id ?? '',
						name: linkedLane.shipper?.name ?? '',
					},
					margin_percentage: linkedLane.margin_percentage ?? '0',
					carrier_lane_id: linkedLane.carrier_lane_id ?? '',
					shipper_lane_id: linkedLane.shipper_lane_id ?? '',
				})
				setTimeout(() => {
					const url = `/configuration/ratecards/linked/update/step2/${linkedLane.carrier_lane_id}?type=linked&cid=${linkedLane.carrier?.external_id}`;
					navigate(url);
				}, 500)
			}
		} catch (err) {
			console.error('Failed to load linked lane configuration:', err);
			toast.error('Failed to load linked rate card configuration');
			navigate(`/configuration/ratecards?type=linked`);
		}
	}

	useEffect(() => {
		// Redirect to linked ratecard list page when missing activeCarrier and activeShipper
		if (isLinked && !activeCarrier && !activeShipper) {
			const { pathname } = location;
			const searchParams = new URLSearchParams(location.search);
			if (pathname.includes('configuration/ratecards/linked')) {
				const laneLinkExternalId = searchParams.get('laneLinkExternalId')
				if (laneLinkExternalId) {
					getLinkedLaneConfigurationAndImpersonate(laneLinkExternalId)
					return;
				}
			}

			navigate(`/configuration/ratecards?type=linked`)
			return;
		}

		if (!activeShipper && !activeCarrier && !isAdminRoute) return

		callGetRCSettings();
		callGetGlobalSettingsAPI();
		if (isCreateRateCard) {
			const searchParams = new URLSearchParams(location.search);
			const idToDuplicate = searchParams.get('idToDuplicate');
			if (laneData) {
				const routes = idToDuplicate ? `${idToDuplicate}` : `${isAdminRoute ? 'admin' : 'customer'}?${laneData}`;
				// Do not refetch if we already have the correct rate card in state
				if (isLinkedCarrierRateCardRoute && rateCard) {
					return;
				}
				callGetLane({
					headers: linkedRateCardExternalId ? { 'X-Customer-Id': linkedRateCardExternalId } : { 'X-Customer-Id': CustomerId },
					routes: [routes]
				})
					.then((response) => {
						if (response?.data) {
							setRcFormErrors(null);
							if (!idToDuplicate) {
								setRateCard({
									...response?.data?.data,
									// TODO remove once lanesNoEndDateFeatureFlag flag is removed
									end_date: (response.data.data.external_id && response.data.data.end_date) ? format(new Date(response.data.data.end_date), DATE_FORMATS.SHORT_DATE) : '',
								});
							} else if (laneDataObject) {
								// Set lane fuel levy for duplication
								const fuelLevies = { ...response.data.data.fuel_levies };
								if (fuelLevies.rate_card?.current) {
									fuelLevies.rate_card.current.start_at = format(new Date(), 'yyyy-MM-dd');
									delete fuelLevies.rate_card.id;
									delete fuelLevies.rate_card?.current.id;
								}

								setRateCard({
									...response?.data?.data,
									// reset external id and lane data so it's not overwritten
									external_id: null,
									origin_location: laneDataObject.origin_location,
									destination_location: laneDataObject.destination_location,
									freight_mode: laneDataObject.freight_mode,
									// TODO remove once lanesNoEndDateFeatureFlag flag is removed
									end_date: (response.data.data.external_id && response.data.data.end_date) ? format(new Date(response.data.data.end_date), DATE_FORMATS.SHORT_DATE) : '',
									fuel_levies: fuelLevies,
									shipper_tag: null,
									latest_cpi_increase: undefined,
								});
							}
						}
					})
					.catch((err) => {
						console.error(err);
						toast.error('Oops, something went wrong trying to fetch this rate card');
					})
			}
			return
		}
		if (laneData) {
			// Do not refetch if we already have the correct rate card in state
			if (isLinkedShipperRateCardRoute && isUpdateRateCard && rateCard && rateCard.external_id === laneData) {
				return;
			}

			// Do not refetch if we already have the correct rate card in state
			if (isLinkedCarrierRateCardRoute && rateCard && rateCard.external_id === laneData) {
				return;
			}
			callGetLane({
				headers: linkedRateCardExternalId ? { 'X-Customer-Id': linkedRateCardExternalId } : { 'X-Customer-Id': CustomerId },
				routes: [laneData]
			})
				.then((response) => {
					if (response?.data) {
						setRcFormErrors(null);
						setRateCard({
							...response?.data?.data,
							// TODO remove once lanesNoEndDateFeatureFlag flag is removed
							end_date: response.data.data.end_date ? format(new Date(response.data.data.end_date), DATE_FORMATS.SHORT_DATE) : ''
						});
					}
				}).catch((err) => {
					console.error(err);
					toast.error('Oops, something went wrong trying to fetch this rate card');
				})
		}
	}, [activeShipper, activeCarrier, isCreateRateCard, laneData, location, isAdminRoute]);

	useEffect(() => {
		if (carrierInRoute && activeCarrier) {
			callGetShipmentTags();
		}
	}, [carrierInRoute, activeCarrier]);

	useEffect(() => {
		if (laneDataObject) {
			if (laneDataObject.shipper_tag) {
				setShipperTag(laneDataObject.shipper_tag);
			}
		}
	}, [laneDataObject])

	useEffect(() => {
		if (rateCard?.shipper_tag) {
			setShipperTag(rateCard?.shipper_tag.external_id);
		}
	}, [rateCard?.shipper_tag])

	if (getErrorRateCardGlobalSettings || getErrorRateCardSettings || getGetLaneError) {
		return <ErrorFallback />
	}

	if (
		isLoadingRateCardGlobalSettings ||
		isLoadingRateCardSettings ||
		isLoadingGetLane ||
		(!activeCarrier && !activeShipper && !isAdminRoute) ||
		!initialLoad
	) {
		return (
			<div className={styles.container}>
				<div className={styles.loader}>
					<Loader />
				</div>
			</div>
		)
	}

	return (
		<>
			<PageTitle title='Ofload | Rate Card' />
			{readonly && (
				<StickyHeader>
					<div className={styles.versions}>
						<div className={styles.format}>
							<span className={styles.item}>
								<VisibilityOutlined className={styles.visible} />
								<span>
									<Text weight={TEXT_WEIGHT.BOLD} text={'You are viewing a historical version.'}/>
									<Text text={'This is a read-only view.'}/>
								</span>
							</span>
							<div className={styles.selector}>
								<Select value={selectedVersion} onChange={setSelectedVersion} name={'version'} placeholder={'Select version'}>
									{ versionOptions?.map((option) => {
										return <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>
									})}
								</Select>
							</div>
						</div>
						<span className={styles.exit}>
							<Logout className={styles.link} />
							<Button text={'Exit Tool'} onClick={backToLiveVersion} type={BUTTON_TYPES.ACTION} size={BUTTON_SIZE.LARGE}/>
						</span>
					</div>
				</StickyHeader>

			)}
			<div className={styles.container} ref={containerRef}>
				<div className={styles.rateCardCrumbs}>
					<Breadcrumbs crumbs={breadCrumbs} lastLinkAsText/>
					{
						!isCreateRateCard && !readonly &&
						<div className={styles.rateCardHistoryLinks}>
							<Button text={'View History'} type={BUTTON_TYPES.TERTIARY} onClick={() => {
								setViewHistory(true)
							}}/>
						</div>
					}
				</div>
				{!canEditRateCard && <>
					<Spacer size={20}/>
					<InfoChip type={CHIP_TYPE.INFO} text='You do not have permission to modify rate cards' />
				</>
				}

				{rateCard?.is_active === false && (
					<>
						<Spacer size={20} />
						<InfoChip
							type={CHIP_TYPE.WARN}
							stretch={true}
							showIcon
							title={'Draft Rate Card'}
							text={`This rate card is awaiting approval before it goes live. If not approved within 7 days, it'll be deleted automatically.`}
						/>
					</>
				)}

				{
					isLinked && <>
						<Spacer size={20} />
						<H1>{isUpdateRateCard ? 'Edit Linked Rate Card' : 'Create Linked Rate Card'}</H1>
						<Spacer size={20} />
						<RateCardStepper activeIndex={isLinkedCarrierRateCardRoute ? 1 : 2}/>
						<Spacer size={20} />
						<LinkedRateCardSummary />
					</>
				}

				<RateCardHeader
					shipperTagName={tagsList?.find((tag) => tag.external_id === shipperTag)?.name || null}
					isDisabled={!canEditRateCard || readonly || isLinkedShipperRateCardRoute}
					isLinkedRateCard={!!isLinked}
				/>
				<Spacer size={20} />
				<Grid columns={2} columnTemplate={'repeat(2, 1fr)'}>
					<Grid.Item>
						<FuelLevySettingsPanel isLinkedRateCard={!!isLinked} isDisabled={!canEditRateCard || readonly || isLinkedShipperRateCardRoute} />
					</Grid.Item>
					<Grid.Item />
				</Grid>
				<Spacer size={20} />
				<ConfigureRates settingType={settingType} isDisabled={!canEditRateCard || readonly || isLinkedShipperRateCardRoute } isLinkedRateCard={isLinkedShipperRateCardRoute || !!isLinked}/>
			</div>

			{!readonly && (
				<StickyFooter>
					<div className={styles.btns}>
						{renderFooterButtons()}
					</div>
				</StickyFooter>
			)}

			{!readonly && (
				<Sidebar
					title={'History'}
					toggleSideBar={(b) => setViewHistory(b)}
					open={viewHistory}
					canCloseFromOutside={true}
					width = '30%'
				>
					{viewHistory && <ShipperRateCardHistory type={settingType} isLinkedRateCard={!!isLinked} laneData={laneData ?? ''} onClose={() => setViewHistory(false)}/>}
				</Sidebar>
			)}

			<Dialog
				open={isApproveDialogOpen}
				onClose={() => setIsApproveDialogOpen(false)}
				title={'Approve rate card'}
				text={'Once approved, this rate card will go live and be used for pricing on this lane.'}
				actionButtonFn={() => {
					handleApprove();
				}}
				actionButtonText={'Approve & Save'}
				actionButtonLoading={loadingApproveRateCards}
			/>

			<Dialog
				open={isDeleteDialogOpen}
				onClose={() => setIsDeleteDialogOpen(false)}
				title={'Delete rate card'}
				text={`This rate card will be permanently deleted. This can't be undone.`}
				actionButtonFn={handleDelete}
				actionButtonText={'Delete'}
				actionButtonLoading={loadingDeleteRC}
			/>

			<Dialog
				title={`${rateCard?.external_id ? 'Update' : 'Create'}  Linked Configuration`}
				text={`You are ${rateCard?.external_id ? 'updating' : 'creating'} a linked configuration. This will apply changes to the following 2 rate cards:`}
				actionButtonText={`${rateCard?.external_id ? 'Update' : 'Create'} Rate Cards`}
				cancelButtonText={'Cancel'}
				open={isAlertInfoOpen}
				actionButtonFn={() => (
					handleSaveClick()
				)}
				actionButtonLoading={loadingCreateLinkedLane || loadingUpdateLinkedLane}
				onClose={() => setIsAlertInfoOpen(false)}
				slot={<>
					{/* Route */}
					<div>
						<div className={styles.linkedRateCardDialogText}><Text text={`Route:`} /> </div>
						<div className={styles.linkedRateCardLocations}>
							{rateCard?.origin_location?.text} <ArrowCircleRightOutlinedIcon color='warning'/> {rateCard?.destination_location?.text}
						</div>
					</div>

					{/* Carrier */}
					<div>
						<div className={styles.linkedRateCardDialogText}><Text text={`Carrier:`} /> </div>
						<H5>{linkedRateCardData?.carrier?.name}</H5>
					</div>

					{/* Shipper */}
					<div>
						<div className={styles.linkedRateCardDialogText}><Text text={`Shipper:`} /> </div>
						<H5>{linkedRateCardData?.shipper?.name}</H5>
					</div>

					{/* 4PL Margin */}
					<div>
						<div className={styles.linkedRateCardDialogText}><Text text={`Margin %`} /> </div>
						<H5>{linkedRateCardData?.margin_percentage}%</H5>
					</div>
				</>}
			/>
		</>
	);
}

